import { judgedDisagreement } from './judged'
import { mathematicalProgressText } from './mathSession'
import { nodeTextRetentionNotes, textTruncationNotes } from './textRetention'
/**
 * The exploration, as a document.
 *
 * Two shapes: the lineage of one node (a chain, meant to be pasted), and the
 * whole graph (a tree, meant to be kept). Both are generated here so they can
 * never disagree about how a node reads.
 *
 * Pure — no React, no store subscription. Two rules govern everything below:
 *
 *  - The document is honest about what it is. The confidence, novelty and
 *    tension numbers are the model's own self-ratings, so they are printed as
 *    raw two-decimal values and captioned as a sort order, never as a percent
 *    and never as a grade.
 *  - The document ignores the display filters, the timeline and the folds, and
 *    says so. An export that silently omitted every contradiction because a
 *    switch was off would be a dishonest artifact.
 */

import type {
  FieldSweep,
  Bridge,
  BridgeEnd,
  Lens,
  Op,
  OpKind,
  PassageCite,
  ReasonNode,
  Session,
  Temper,
} from './types'
import { BRIDGE_RUNGS_TOTAL, RUNG_LABEL, STOP_LABEL, bridgeStanding, sortBridges } from './rung'
import {
  DIRECTIVE_LABEL,
  BEARING_LABEL,
  CORPUS_ONLY_CAVEAT,
  LENS_LABEL,
  MOVE_REASON,
  moveResult,
  OP_LABEL,
  OP_PAST,
  READING_LABEL,
  RUN_STOP_HEAD,
  corpusMissNote,
  checkedAgainstSources,
  checksSkippedLine,
  checksUnreadLine,
  modelNames,
  modelPassesFor,
  modelsUsed,
  nextStepsOf,
  previousReasoningOf,
  reasoningOf,
  sourceReach,
  readingCaveat,
  readingNote,
  refutedBacking,
  outcomeLive,
  retractedWorks,
  totalSpend,
  kindLabelOf,
} from './types'
import {
  refutedUnder,
  refutedUnderSentence,
  rewrittenOver,
  rewrittenOverSentence,
  taintMap,
} from './taint'
import { hostOf } from './url'
import {
  CONJECTURE_MOVE_LABEL,
  closeSentence,
  predictionClauses,
  registrationLive,
} from './conjecture'
import {
  laterStatements,
  latestPriorArt,
  measuredSentence,
  priorArtLabel,
  priorArtLive,
  priorArtNote,
} from './priorArt'
import { SWEEP_KIND_LABEL, latestSweep, sweepSentence } from './sweep'
import {
  EXECUTABLE_METHOD_LABEL,
  EXECUTED_NOT_A_RESULT,
  conjectureContracts,
  executedSentence,
  rangesSentence,
} from './executableConjecture'
import { liveOrigin, liveOriginName, liveOriginUrl } from './sources'
import { PRICE_READ_ON, fmtUsd } from './models'
import { PANEL_VERDICT_LABEL, missingLenses, panelSplit, panelSummary } from './panel'
import type { ModelStability } from './panel'
import type { TrackRecordRow } from './trackRecord'
import { childrenIndex, lineage } from './store'
import type { ModelCalibration } from './calibration'
import {
  EARLIER_SURVEYS_TITLE,
  SURVEY_HEADING,
  earlierSurveyBlocks,
  evidenceLedgerNotes,
  removedVerificationIntro,
  removedVerificationNotes,
  reportBody,
  reportDocument,
  surveyBlocks,
  surveySourceTitle,
  verifiedLive,
  walk,
  type Block,
  type Rich,
  type RunStyle,
  type SourceLine,
} from './reportSections'
import { surveyLive } from './survey'
import { surveyRecord } from './recordDigest'
// The shared arithmetic moved to `reportSections.ts` with the report's sections;
// re-exported so every caller that read it from here still does.
export {
  READING_ORDER,
  evidenceLedgerNotes,
  removedVerificationIntro,
  removedVerificationNotes,
  shapeOf,
  staleUnderCount,
  subtreeSizes,
  verifiedLive,
  walk,
} from './reportSections'
export type { Row, Shape, Walk } from './reportSections'
import { opLabel } from './narrationReceipt'
import { priorArtOf, priorArtText } from './priorWork'
import {
  CLOSENESS_WORD,
  DESIGN_WORD,
  PRIOR_ART_CLOSENESS_WORD,
  STATUS_WORD,
  citesRetractedCount,
  stanceLabel,
} from './vocabulary'
import { runReadings } from './runFound'
import { calendarDay } from './dates'
import { briefLateNote, briefLeadClause } from './brief'
import { evidenceClause } from './claimEvidence'
import { fmtRating, rewriteAuthorship, selfRatingsOf } from './rewrite'
import {
  ENDING_LABEL,
  FRAME_NOTE,
  OBJECTION_STOOD_BELOW,
  endingLabelOf,
  endingOf,
  endingSummary,
  objectionsStoodUnder,
  reasonedEndingOf,
  runEndingsLine,
} from './endings'

/**
 * A candidate's standing, for the line beside its title in every export.
 *
 * The rung first and the model's hint last, in words that say which is which:
 * the rung was earned from records, the hint is the proposing model's opinion
 * of its own proposal, and an export that printed only the number was the
 * leaderboard the ladder replaced (D82).
 */
function standingLine(b: Bridge, nodes: Session['nodes']): string {
  const st = bridgeStanding(b, nodes)
  const where = st.stop
    ? `stopped: ${STOP_LABEL[st.stop].toLowerCase()}`
    : `standing at ${RUNG_LABEL[st.rung].toLowerCase()}, ${st.earned.length} of ${BRIDGE_RUNGS_TOTAL} rungs earned`
  return `${where} · scan hint ${b.score.toFixed(2)}`
}

export interface MarkdownOptions {
  /** Injected so output is deterministic under test. */
  now?: number
  /** Include the retained reasoning traces. Default true; a no-op with none. */
  thinking?: boolean
  lens?: Lens
  temper?: Temper
  /**
   * Measured calibration for the models in this graph, from every exploration in
   * the library — the archive's are not counted until they are opened again.
   *
   * Passed IN rather than read here, and that is the whole point: this module is
   * pure over a `Session` so that anyone holding the same graph can re-derive
   * the same document, which is the property every section of the report rests
   * on. A function reaching into storage would quietly break it — two people
   * with identical graphs would get different documents. Absent means the
   * section is omitted, which is also what a caller wanting a graph-only
   * document should pass. The app's callers read all three tables through one
   * call, `libraryLedger` in `persist.ts`, which reads the open exploration live
   * — so a result recorded a moment ago is in the report as it is in the Library.
   */
  calibration?: ModelCalibration[]
  /**
   * The same arithmetic over source-check readings instead of recorded results.
   *
   * A SECOND table and never merged into the one above, because the two answer
   * different questions and one of them outranks the other: a recorded result is
   * something somebody observed, a reading is what a search returned on a date. Passed
   * in for the same purity reason `calibration` is — see above.
   */
  checkCalibration?: ModelCalibration[]
  /**
   * How often each model's own panels agreed with themselves, across the library.
   *
   * A THIRD table, beside the two above and answering a different question: those
   * two grade a confidence against something outside the model, this counts how
   * far three blind passes of one model landed apart. Never averaged with either —
   * a model can be perfectly steady and perfectly wrong. Passed in for the same
   * purity reason the other two are.
   */
  stability?: ModelStability[]
  /** The conjecture track record, library-wide (`core/trackRecord.ts`). Passed in
   *  for the calibration tables' purity reason; filtered to this graph's models. */
  trackRecord?: TrackRecordRow[]
}

const OP_KINDS = new Set<string>(Object.keys(OP_LABEL))
const isOpKind = (v: string): v is OpKind => OP_KINDS.has(v)

/* ── text ─────────────────────────────────────────────────────────────── */

export function slug(s: string, fallback: string): string {
  const out = s
    .slice(0, 40)
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
  return out || fallback
}

export function markdownFilename(session: Session, node?: ReasonNode): string {
  const tail = node ? `-${slug(node.label, node.id)}` : ''
  return `pathways-${slug(session.seed, session.id)}${tail}.md`
}

/**
 * Escape the characters that actually bite in this corpus.
 *
 * `_` and `~` are not optional: the shipped material contains `G_μν`, `⟨T_00⟩`
 * and `~10⁶⁴ kg`, and a single tilde strikes through on GitHub while an
 * intraword underscore italicises in several other renderers.
 */
function esc(s: string): string {
  return s.replace(/[\\`*_[\]<>|~]/g, (c) => `\\${c}`)
}

/** `esc`, plus whatever stops a paragraph being read as block syntax. */
function escBlock(s: string): string {
  // Per line, not just the first: `reasoning` is multi-paragraph and is emitted
  // at column zero, so a later line beginning `1.` would silently become an
  // ordered list.
  return esc(s.replace(/\r\n/g, '\n'))
    .trim()
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((line) => (/^(#|-|\+|>|\d+\.)/.test(line) ? `\\${line}` : line))
    .join('\n')
}

/** The seed is a free multi-line textarea, and front matter is not. */
function yaml(s: string): string {
  return JSON.stringify(s.replace(/[\r\n]+/g, ' '))
}

/** A heading is one line, and a seed or label can contain newlines. */
function head(s: string): string {
  return escBlock(s).replace(/\s*\n+\s*/g, ' ')
}

/** The reader's calendar day — the day the app shows for the same record. */
function fmt(t: number): string {
  return calendarDay(t)
}

function appendRemovedVerificationSection(out: string[], session: Session): void {
  const notes = removedVerificationNotes(session)
  if (!notes.length) return
  out.push('')
  out.push('## Checks on removed claims')
  out.push('')
  out.push(removedVerificationIntro(notes.length))
  out.push('')
  out.push(notes.map((note) => `- ${escBlock(note)}`).join('\n'))
}

/**
 * Which op produced which node.
 *
 * Deliberately not the store's `producingOp`: this one skips the root, because
 * the demo's opening op lists the root in `produced` and a document that said
 * the seed was "branched" would be wrong.
 */
function producerIndex(session: Session): Map<string, Op> {
  const m = new Map<string, Op>()
  for (const op of session.ops) {
    for (const id of op.produced) {
      const n = session.nodes[id]
      if (n && n.parentId !== null && !m.has(id)) m.set(id, op)
    }
  }
  return m
}

/**
 * The move a node came from.
 *
 * The op join is required, not a nicety: every initially-loaded demo node
 * carries `origin: 'seed'` despite being a child of the root, so reading origin
 * alone would label twenty-five nodes "from Seed".
 */
function moveOf(n: ReasonNode, prod: Map<string, Op>): string {
  const kind = prod.get(n.id)?.kind ?? (isOpKind(n.origin) ? n.origin : 'expand')
  return OP_PAST[kind]
}

/**
 * `showModel` is decided ONCE per document, from `modelsUsed(session).length > 1`.
 *
 * A per-node test would print the model on every node of a single-model graph,
 * where it is noise; deciding per document is what makes it information. On a
 * mixed graph a node with no stamp prints an em dash rather than being attributed
 * to whichever model happens to be selected now.
 */
function metaLine(n: ReasonNode, prod: Map<string, Op>, showModel = false): string {
  const bits = [kindLabelOf(n), `from ${moveOf(n, prod)}`]
  // A rewritten node's words are the reader's; the stamp names who drafted what
  // they replaced, and "reasoned by" alone would credit the model with both.
  if (showModel) bits.push(rewriteAuthorship(n, false) ?? `reasoned by ${n.model?.name ?? '—'}`)
  // A rewrite replaces the claim and never re-rates it, so the old numbers
  // describe wording that is no longer there — the card drops them by the same
  // helper. A node carried from an insight (a conjecture) was never rated at all.
  const rated = selfRatingsOf(n)
  bits.push(
    rated
      ? `self-rated ${fmtRating(rated.confidence)} confidence, ${fmtRating(rated.novelty)} novelty, ${fmtRating(rated.tension)} tension`
      : n.editedAt === undefined && n.carriedFrom
        ? 'no self-ratings — carried from an insight, and no model rated it'
        : 'self-ratings dropped, they described the model’s wording',
  )
  return `*${bits.join(' · ')}*`
}

/**
 * A recorded result, as a document block.
 *
 * First after the claim, and by a distance the most important thing an exported
 * document can carry: everything else in here is the model's reasoning and this is
 * the one fact that came from the world. Set as a quotation, verbatim, never
 * paraphrased.
 */
function outcomeBlock(n: ReasonNode): string[] {
  const o = n.outcome
  if (!o) return []
  const out: string[] = []
  const moved = Math.abs(o.confidenceBefore - n.confidence) > 0.005
  out.push(
    `**A result was recorded here on ${fmt(o.recordedAt)}** — ${BEARING_LABEL[o.bearing].toLowerCase()}, observed ${fmt(o.at)}${o.source ? `, from ${escBlock(o.source)}` : ''}.`,
  )
  out.push(`> ${escBlock(o.observed).replace(/\n/g, '\n> ')}`)
  out.push(
    moved
      ? `*Confidence was moved from ${o.confidenceBefore.toFixed(2)} to ${n.confidence.toFixed(2)} by hand. The first number was the model's.*`
      : `*Confidence left at ${n.confidence.toFixed(2)}. The tool suggests no number for a recorded result.*`,
  )
  return out
}

/**
 * Where a cited passage came from, in one clause, for both documents.
 *
 * An export is the artefact that leaves the app, which is exactly where a
 * fetched record masquerading as the reader's own material does the most damage:
 * whoever opens the file cannot ask. The report already printed "(your own
 * document)" over every cite and the verification block printed nothing at all,
 * so both said the same wrong thing about a passage this app found a moment ago.
 * One clause, one derivation — `liveOrigin`, off the stored id — and the same
 * words in both places, because two documents describing one record differently
 * is its own bug.
 */
const citeOrigin = (c: PassageCite): string => {
  const o = liveOrigin(c.docId)
  if (!o) return 'your own document'
  // `doi:` names no row, so the clause says the passage was fetched and stops
  // there rather than naming a service that may not have been the one.
  const from = liveOriginName(o)
  return from ? `fetched during this check from ${from}` : 'fetched during this check'
}

/**
 * The cited document's title, as a link where the record has a public address.
 *
 * The web sources a few lines down have been `[title](url)` since the day they
 * were written, and a passage from a fetched record was the one cited thing in
 * either document a reader could not open — worst here, because an export is read
 * by somebody who cannot ask, cannot hover and cannot re-run the check.
 * `liveOriginUrl` derives the address off the stored `docId`, so a document
 * exported from a check run months ago carries the links too.
 *
 * The user's own documents stay bold and unlinked, which is not an omission: there
 * is no address for a file on their machine, and inventing one would be the first
 * dishonest link in either document.
 */
const citeTitle = (c: PassageCite): string => {
  const url = liveOriginUrl(c.docId)
  return url ? `[**${esc(c.docTitle)}**](${url})` : `**${esc(c.docTitle)}**`
}

/** What a source check found, as a document block. */
function verificationBlock(n: ReasonNode, session: Session): string[] {
  const v = n.verification
  if (!v) return []
  const out: string[] = []
  const stale = n.editedAt !== undefined && v.checkedAt < n.editedAt
  out.push(
    `**Checked against sources on ${fmt(v.checkedAt)} with ${esc(v.model.name)}: ${READING_LABEL[v.reading].toLowerCase()}.** ${readingNote(v)}`,
  )
  // The card's clause under its reading — "5 reports · 1 independent: 1
  // observational study, 4 news reports" — asked as the card asks it, so the
  // record and the card cannot count one claim two ways. Nothing on a check of
  // an earlier wording: its sources are not this claim's.
  const clause = evidenceClause(session, n.id)
  if (clause) out.push(esc(clause))
  // What is actually behind a refutation, in the model's own terms: a quote checked
  // against the page, a page that was merely opened, or a page whose text never came
  // back in a readable form. Three strengths, never one sentence.
  const backing = refutedBacking(v)
  if (backing) out.push(`*${backing}*`)
  if (stale) {
    out.push(
      '*The claim above was rewritten after this check ran, so what was searched for is not what it now says.*',
    )
  }
  const caveat = readingCaveat(v)
  if (caveat) out.push(`*${esc(caveat)}*`)
  if (v.summary) out.push(escBlock(v.summary))
  out.push(
    ...[v, ...v.sources, ...(v.corpus?.cites ?? [])].flatMap(textTruncationNotes).map(escBlock),
  )
  if (v.sources.length) {
    // The quote is quoted only when it was checked against the page it is supposed
    // to be on. The other two states are different findings — a wording that was
    // looked for and not found, and a wording nothing could look for — and printing
    // either inside quotation marks would claim a check that never happened.
    // The judge's second reading, where it disagrees with the model's stance and
    // is sure enough to print — the inspector's rule, `judgedDisagreement`, in the
    // artefact that gets pasted somewhere nobody can open the inspector.
    const judgedLine = (s: (typeof v.sources)[number]): string => {
      const j = judgedDisagreement(s)
      if (!j) return ''
      const read = j.stance === 'neither' ? 'bearing on something else' : 'supporting it'
      return `; ${esc(j.model.name)} read the quote as ${read} (${j.probabilities[j.stance].toFixed(2)})`
    }
    const quoteLine = (s: (typeof v.sources)[number]): string => {
      if (!s.quote) return ''
      if (s.quoteFound === true) return `\n  “${escBlock(s.quote)}”`
      if (s.quoteFound === false) {
        return `\n  ${escBlock(s.quote)} — this wording was not found on the page`
      }
      return `\n  ${escBlock(s.quote)} — the quote could not be checked against the page`
    }
    out.push(
      v.sources
        .map(
          // The host, printed as its own field rather than left inside the link.
          //
          // The inspector has always shown it; the report did not, so once a
          // report left the app a refutation from `random-blog.example` and one
          // from a journal read identically — and the report is precisely the
          // artefact that gets pasted somewhere else, where nobody can hover a
          // link to see where it goes.
          //
          // On every source and not only a disputing one, because that is what the
          // inspector does and two surfaces printing the same record differently is
          // its own bug. It also costs nothing: unlike the two-domain gate, showing
          // a domain has no false negatives.
          //
          // What kind of evidence the check read the page as rides beside the
          // host, as on the report's source lines (`sourceLinesOf`) — only where
          // it labelled one, never "unclear".
          (s) =>
            `- [${esc(s.title)}](${s.url}) — ${hostOf(s.url)}${s.design ? ` · ${DESIGN_WORD[s.design][0]}` : ''} · ${sourceReach(s, v)}${s.stance === 'disputes' ? ', disputes this' : s.stance === 'supports' ? ', supports this' : ''}${judgedLine(s)}${quoteLine(s)}`,
        )
        .join('\n'),
    )
    if (v.judgeNote) out.push(esc(v.judgeNote))
  }
  if (v.corpus?.cites.length) {
    out.push(
      v.corpus.cites
        .map(
          (c) =>
            `- ${citeTitle(c)}, passage ${c.passage} (${citeOrigin(c)}) — “${escBlock(c.quote)}”`,
        )
        .join('\n'),
    )
  }
  // A reported result's matched study is its source, not prior work, and is
  // listed under that name with nothing said about novelty (`priorWork.ts`).
  if (v.priorArt.length) {
    out.push(
      n.kind === 'evidence'
        ? '**The source the check found for this result**'
        : '**Prior work the check found**',
    )
    out.push(
      v.priorArt.map((a) => `- ${escBlock(a.covers)} (${CLOSENESS_WORD[a.closeness]})`).join('\n'),
    )
  }
  // The one line every surface that prints a novelty prints beside it: the closest
  // kept source by title, and the two numbers apart, never blended.
  const art = priorArtOf(n)
  if (art) {
    out.push(
      `*${esc(priorArtText(art))}.${art.kind === 'prior' && art.rerated?.from !== undefined ? ' Both numbers are the model’s own judgement; the second had something to look at.' : ''}*`,
    )
  }
  const prov = [
    v.queries.length ? `${v.queries.length} search${v.queries.length === 1 ? '' : 'es'}` : null,
    // Only where something searched. On a documents-only pass "0 results returned"
    // describes a search that never ran — the same gate the inspector applies, for
    // the same reason.
    v.scope === 'corpus' ? null : `${v.seen} result${v.seen === 1 ? '' : 's'} returned`,
    v.dropped
      ? v.scope === 'corpus'
        ? `${v.dropped} named source${v.dropped === 1 ? '' : 's'} dropped, since no search ran to return ${v.dropped === 1 ? 'it' : 'them'}`
        : `${v.dropped} named source${v.dropped === 1 ? '' : 's'} dropped as not in the results`
      : null,
    v.partial ? 'cut off at the search limit, so a partial look' : null,
    // The same miss the inspector prints, from the same helper: a report that
    // dropped it would read as a check that never had documents to search.
    corpusMissNote(v),
  ].filter(Boolean)
  if (prov.length) out.push(`*${prov.join(' · ')}.*`)
  // What did not complete, a sentence a step, as the inspector prints it under the
  // counts. A `not-checkable` the gate set rests on exactly these, and without them
  // the document would say that a search failed but not which step (D326).
  if (v.failures?.length) out.push(`*${v.failures.map((f) => esc(f)).join(' ')}*`)
  // The same sentence the inspector prints, from the same constant, because an
  // exported document that omitted it would be a stronger claim than the screen.
  if (v.scope === 'corpus') out.push(`*${CORPUS_ONLY_CAVEAT}*`)
  for (const note of evidenceLedgerNotes(n)) out.push(`*${escBlock(note)}*`)
  return out
}

/** What an adversarial panel found, as a document block. */
function panelBlock(n: ReasonNode): string[] {
  const p = n.panel
  if (!p) return []
  const out: string[] = []
  out.push(
    p.model
      ? `**Read again under ${p.lenses.length} lenses on ${fmt(p.checkedAt)} with ${esc(p.model.name)}.** ${panelSummary(p)}`
      : `**Read again under ${p.lenses.length} lenses on ${fmt(p.checkedAt)}.** ${panelSummary(p)}`,
  )
  out.push(
    p.votes
      .map(
        (v) =>
          `- **${LENS_LABEL[v.lens]}** — ${PANEL_VERDICT_LABEL[v.verdict].toLowerCase()} (${v.confidence.toFixed(2)}): ${escBlock(v.because)}`,
      )
      .join('\n'),
  )
  // By name, never by count: the votes above are the seats that RETURNED, so
  // slicing the lens list by how many arrived names the wrong seat whenever an
  // early one failed. Same function the sheet's rows read, so the document and
  // the screen cannot name different lenses for the same panel.
  const missing = missingLenses(p)
  if (missing.length) {
    out.push(`*${missing.map((l) => LENS_LABEL[l]).join(', ')} did not return a verdict.*`)
  }
  return out
}

/** A sentence-initial form of an ending or line from `endings.ts`. */
const initial = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * A conjecture's record, as a document block, headed with the reader's word for
 * one, Insight (D333).
 *
 * After the node's reasoning, because it is part of what the node SAYS: what it
 * departs from, the one step its author recalls no source taking, the
 * observation that separates the two, and the test that would kill it — with
 * the date its wording was registered. No sentence here says it is new, and the
 * block says in so many words that nothing has searched for it: an exported
 * conjecture is read by somebody who cannot ask, and silence there would read
 * as a claim.
 */
function conjectureBlock(
  n: ReasonNode,
  session: Pick<Session, 'id' | 'research' | 'ops'>,
): string[] {
  const c = n.conjecture
  if (!c) return []
  const p = predictionClauses(c.prediction)
  const earlier = c.registrations?.length ?? 0
  const out = [
    `**Insight** — ${esc(CONJECTURE_MOVE_LABEL[c.move]).toLowerCase()}. Registered ${fmt(c.registered.at)}${
      earlier
        ? `, after ${earlier} earlier registration${earlier === 1 ? '' : 's'} a rewrite replaced`
        : ''
    }.`,
    [
      `- **Departs from:** ${escBlock(c.departsFrom)}`,
      `- **The leap:** ${escBlock(c.leap)}`,
      `- **Predicts:** ${closeSentence(`if ${escBlock(p.condition)}, ${escBlock(p.observable)} shows ${escBlock(p.expected)} — where the expectation it departs from says ${escBlock(p.consensusExpects)}`)}`,
      `- **Killed by:** ${escBlock(c.killTest)}`,
    ].join('\n'),
    ...priorArtBlock(n),
    ...executableBlock(n, session),
  ]
  if (!registrationLive(n)) {
    out.push('*The wording above is not the wording that was registered.*')
  }
  return out
}

/**
 * The executable tests on a conjecture, as a document block (D283): what each
 * contract measures and declared before it ran, and what each receipt read —
 * with who wrote the evaluator, every time, and the sentence saying a reading is
 * not a recorded result. Nothing when no contract tests it: most kill tests are
 * not computations, and silence here claims nothing.
 */
function executableBlock(
  n: ReasonNode,
  session: Pick<Session, 'id' | 'research' | 'ops'>,
): string[] {
  const contracts = conjectureContracts(session, n)
  if (!contracts.length) return []
  const rows = contracts.flatMap((c) => [
    `- **${esc(c.spec.title)}**${
      c.design
        ? ` — ${EXECUTABLE_METHOD_LABEL[c.design.method]}, written by ${esc(c.design.model)}`
        : ''
    }. It measures ${esc(c.spec.metric.name)} in ${esc(c.spec.metric.unit)}; ${esc(rangesSentence(c))}.${
      c.wording === 'earlier' ? ' It was written for an earlier wording of this insight.' : ''
    }`,
    ...(c.results.length
      ? c.results.map((r) => `  - ${esc(executedSentence(r))}`)
      : ['  - Not run.']),
  ])
  return [
    '*Executable tests — a model wrote each contract’s programs, data and evaluator, and nobody has reviewed them.*',
    rows.join('\n'),
    `*${esc(EXECUTED_NOT_A_RESULT)}*`,
  ]
}

/**
 * The newest prior-art search on a conjecture, as a document block — or the
 * sentence saying none has run. The reading in the app's own words, the pages it
 * listed and the ones the backstop found close, each with its host, so a reader
 * who cannot ask can still open every page the reading rests on.
 */
function priorArtBlock(n: ReasonNode): string[] {
  const s = latestPriorArt(n)
  if (!s) return ['*Whether anyone has already said this has not been searched.*']
  const q = s.queries.length
  const later = n.conjecture ? laterStatements(n.conjecture, s) : []
  const out = [
    `*Prior-art search, ${fmt(s.searchedAt)}, ${q} quer${q === 1 ? 'y' : 'ies'}, by ${esc(s.model.name)}: ${esc(priorArtLabel(s, later))}. ${esc(priorArtNote(s, undefined, later))}${
      priorArtLive(n, s) ? '' : ' It searched wording since rewritten.'
    }*`,
  ]
  const rows = [
    ...s.entries.map(
      (e) =>
        `- [${esc(e.title)}](${e.url}) — ${PRIOR_ART_CLOSENESS_WORD[e.closeness]}, ${hostOf(e.url)}${
          e.quoteFound === false ? '; its quoted sentence is not on the page' : ''
        }${later.includes(e) ? `; stated after this was registered, dated ${e.year} by the page itself` : ''}`,
    ),
    ...s.backstop.hits.map(
      (h) => `- [${esc(h.title)}](${h.url}) — close by term overlap, ${hostOf(h.url)}`,
    ),
  ]
  if (rows.length) out.push(rows.join('\n'))
  const measured = measuredSentence(n, s)
  if (measured) out.push(`*${esc(measured)}*`)
  return out
}

/**
 * The field sweeps on a document's nodes, newest per node, as a section of
 * their own — reportage, printed apart from the reasoning so no item reads as a
 * step in it. `only` limits it to a branch's nodes. Empty when there is none.
 */
function sweepSection(session: Session, heading: string, only?: ReadonlySet<string>): string[] {
  const nodeIds = [
    ...new Set((session.sweeps ?? []).map((s) => s.nodeId).filter((id) => !only || only.has(id))),
  ]
  const latest = nodeIds
    .map((id) => latestSweep(session, id))
    .filter((s): s is FieldSweep => !!s)
    .sort((a, b) => a.searchedAt - b.searchedAt)
  if (!latest.length) return []
  const out = [
    '',
    `## ${heading}`,
    '',
    '*Each item is what one page a search returned says is unsettled, quoted from it — reportage, not a finding about the field, and never a step in the reasoning above.*',
  ]
  for (const sweep of latest) {
    const on = session.nodes[sweep.nodeId]
    const q = sweep.queries.length
    out.push('')
    out.push(
      `### On “${esc(on?.label ?? sweep.subjectAtSweep)}” — ${fmt(sweep.searchedAt)}, ${esc(sweep.model.name)}, ${q} quer${q === 1 ? 'y' : 'ies'}`,
    )
    out.push('')
    out.push(escBlock(sweepSentence(sweep)))
    if (sweep.items.length) {
      out.push('')
      out.push(
        sweep.items
          .map(
            (i) =>
              `- **${SWEEP_KIND_LABEL[i.kind]}**${i.year !== undefined ? ` (${i.year})` : ''} — ${escBlock(i.statement)} “${escBlock(i.quote)}” — [${esc(i.title)}](${i.url}), ${hostOf(i.url)}${i.quoteFound ? '' : '; the quote could not be checked against the page'}`,
          )
          .join('\n'),
      )
    }
  }
  return out
}

/** One node's body, shared by both documents so they can never fork. */
function nodeBody(n: ReasonNode, session: Session): string[] {
  const out: string[] = []
  out.push(...nodeTextRetentionNotes(n).map(escBlock))
  const reasoning = reasoningOf(n)
  const previous = previousReasoningOf(n)
  const nextSteps = nextStepsOf(n)
  out.push(`> ${escBlock(n.claim).replace(/\n/g, '\n> ')}`)
  if (n.editedAt) {
    out.push(
      `**${STATUS_WORD.revisedByYou}** on ${fmt(n.editedAt)}${n.model ? ` · first proposed by ${esc(n.model.name)}` : ''} — the claim above is your wording, and everything below it was re-derived from it.`,
    )
  }
  // What it was reasoned from that has since been refuted: the card's clause and
  // its provenance fold, in their one sentence. The report listed these; the
  // document that carries "everything" printed each such claim as though nothing
  // above it had moved.
  const refuted = refutedUnder(session, n.id)
  if (refuted) {
    out.push(
      `**${STATUS_WORD.standsOnRefuted}.** ${escBlock(refutedUnderSentence(refuted))} That does not make it wrong.`,
    )
  }
  // A claim rewritten after that refutation is not owed a re-derivation, and the
  // record says why in the card's sentence rather than falling silent on it.
  const rewritten = refuted || n.editedAt === undefined ? null : rewrittenOver(session, n.id)
  if (rewritten && n.editedAt !== undefined) {
    out.push(
      `**${STATUS_WORD.rewrittenOverRefuted}.** ${escBlock(rewrittenOverSentence(rewritten, n.editedAt))}`,
    )
  }
  // Order: what happened, then what sources say, then what the panel found, then
  // the model's own reasoning and ratings. Strongest authority first.
  out.push(...outcomeBlock(n))
  out.push(...verificationBlock(n, session))
  out.push(...panelBlock(n))
  if (n.foreignFrom) {
    out.push(
      `*Fused with **${esc(n.foreignFrom.label)}**, from a separate exploration of “${esc(n.foreignFrom.seed)}” — copied on ${fmt(n.foreignFrom.at)}.*`,
    )
  }
  if (reasoning) out.push(escBlock(reasoning))
  out.push(...conjectureBlock(n, session))
  // Kept for the record and labelled for what it is: an argument for the words
  // the reader replaced, which the card folds away and no pass is given.
  if (previous) {
    out.push(
      `*${previous.by === 'you' ? 'Your' : 'The model’s'} reasoning for the previous wording:* ${escBlock(previous.text)}`,
    )
  }
  if (n.compact && !n.elaboration) {
    out.push(
      `*${STATUS_WORD.compactLeaf} — this is the authored reasoning kernel; no later detail was requested.*`,
    )
  } else if (n.elaboration) {
    out.push(
      `*Detailed later by ${esc(n.elaboration.model.name)} in operation ${esc(n.elaboration.opId)}.*`,
    )
  }
  if (n.evidence.length) {
    out.push('**Evidence**')
    out.push(n.evidence.map((e) => `- ${escBlock(e)}`).join('\n'))
  }
  if (nextSteps.length) {
    out.push('**What would move this**')
    out.push(nextSteps.map((e) => `- ${escBlock(e)}`).join('\n'))
  }
  // The parent's half of an objection that stood, said as reasoning — the
  // objection is the model arguing with its own graph, never a refutation.
  const stood = objectionsStoodUnder(session, n.id)
  if (stood.length) {
    out.push(
      `*${initial(OBJECTION_STOOD_BELOW)}: ${stood.map((o) => `**${esc(o.label)}**`).join(', ')}.*`,
    )
  }
  // The ending the model gave the path, in the shared vocabulary. A result
  // recorded since is printed above as the result it is; the model's reason for
  // closing the path stays its own.
  const reasoned = reasonedEndingOf(n)
  if (reasoned) {
    out.push(
      `**${initial(ENDING_LABEL[reasoned])}.** ${escBlock(n.terminalReason || 'Reasoned out.')}${n.outcome && endingOf(n) === 'answered' ? ` *(${ENDING_LABEL.answered} since — see above.)*` : ''}`,
    )
  }
  return out
}

/* ── document (a): one line of reasoning ──────────────────────────────── */

export function branchMarkdown(session: Session, nodeId: string, opts?: MarkdownOptions): string {
  const now = opts?.now ?? Date.now()
  const path = lineage(session, nodeId)
  if (!path.length) return ''
  const prod = producerIndex(session)
  const last = path[path.length - 1]!
  const kids = childrenIndex(session)
  const childCount = (kids.get(last.id) ?? []).length
  const hasTrace = session.ops.some((o) => !!o.thinking)
  // Decided once, per document, exactly as the full export decides it — so one node
  // cannot name its model in one document and stay silent in the other.
  const mixed = modelsUsed(session).length > 1
  const lastEnding = endingLabelOf(last)

  const out: string[] = []
  out.push(`# ${head(session.seed || last.label)}`)
  out.push('')
  out.push(
    `*One line of reasoning from a Pathways exploration — ${path.length} step${path.length === 1 ? '' : 's'}, from the seed to “${esc(last.label)}”${lastEnding ? `, which ended: ${lastEnding}` : ''}. ${
      mixed
        ? "Confidence, novelty and tension below are each model's own self-rating, so they are not comparable between nodes produced by different models — a rough sort order within one model, and not a probability."
        : "Confidence, novelty and tension below are the model's own self-ratings, calibrated by instruction rather than measurement: a sort order, not a probability."
    }*`,
  )
  if (path.length >= 4) {
    out.push('')
    out.push(`**The line:** ${path.map((n) => esc(n.label)).join(' → ')}`)
  }
  out.push('')
  out.push('---')

  path.forEach((n, i) => {
    out.push('')
    out.push(`## ${i + 1} · ${head(n.label)}`)
    out.push('')
    // The seed's claim, label and text are the same string and it has no
    // reasoning, so printing it as a node would be three copies of one line.
    if (i === 0 && n.parentId === null) {
      out.push('The seed.')
      return
    }
    out.push(metaLine(n, prod, mixed))
    out.push('')
    out.push(nodeBody(n, session).join('\n\n'))
  })

  out.push(...sweepSection(session, 'Field sweeps along this line', new Set(path.map((n) => n.id))))

  out.push('')
  out.push('---')
  out.push('')
  out.push('## Where this leaves it')
  out.push('')
  const lastReasoned = reasonedEndingOf(last)
  if (lastReasoned) {
    out.push(
      `**${escBlock(last.label)}** — ${ENDING_LABEL[lastReasoned]}: ${escBlock(last.terminalReason || 'Reasoned out.')}${endingOf(last) === 'answered' ? ` Since then, ${ENDING_LABEL.answered}.` : ''}`,
    )
  } else if (endingOf(last) === 'answered') {
    out.push(`**${escBlock(last.label)}** — ${ENDING_LABEL.answered}.`)
  } else if (childCount) {
    out.push(
      `**${escBlock(last.label)}** is still open. ${childCount} branch${childCount === 1 ? '' : 'es'} continue from here — see the full export.`,
    )
  } else {
    out.push(`**${escBlock(last.label)}** is still open. Nothing has been derived from it yet.`)
  }

  const open: string[] = []
  path.forEach((n, i) => {
    for (const step of nextStepsOf(n))
      open.push(`- ${escBlock(step)} — from step ${i + 1}, ${esc(n.label)}`)
  })
  if (open.length) {
    out.push('')
    out.push('## Still open along this line')
    out.push('')
    out.push(open.join('\n'))
  }

  out.push('')
  out.push('---')
  out.push('')
  out.push(
    `*Exported ${fmt(now)} from Pathways.${hasTrace ? ' The model’s thinking for these moves is in the full export.' : ''}*`,
  )
  return `${out.join('\n')}\n`
}

/* ── document (b): the whole exploration ──────────────────────────────── */

export function explorationMarkdown(session: Session, opts?: MarkdownOptions): string {
  const now = opts?.now ?? Date.now()
  const { rows, numOf, detached } = walk(session)
  const prod = producerIndex(session)

  const body = rows.filter((r) => r.node.parentId !== null)
  const dead = body.filter((r) => r.node.terminal)
  const gaps = body.filter((r) => r.node.kind === 'gap' || r.node.kind === 'experiment')
  const insights = body.filter((r) => r.node.kind === 'insight' || r.node.kind === 'synthesis')
  const edited = body.filter((r) => !!r.node.editedAt)
  const collapsed = body.filter((r) => r.node.collapsed)
  // Grounding, counted the same way the sidebar counts it — `checkedAgainstSources`
  // rather than `!!verification`, so a reading that checked nothing is not counted
  // as a check.
  const checked = body.filter((r) => checkedAgainstSources(r.node))
  const disputed = body.filter((r) => r.node.verification?.reading === 'refuted')
  const measured = body.filter((r) => !!r.node.outcome)
  const split = body.filter((r) => panelSplit(r.node.panel))

  const kids = childrenIndex(session)
  // How many descendants this DOCUMENT prints under a folded node — a different
  // question from the caption's count, which promises what one unfold reveals.
  const printedUnder = (id: string): number => {
    let c = 0
    const st = [...(kids.get(id) ?? [])]
    const seen = new Set<string>()
    while (st.length) {
      const k = st.pop()!
      if (seen.has(k)) continue
      seen.add(k)
      if (!session.nodes[k]) continue
      c++
      for (const g of kids.get(k) ?? []) st.push(g)
    }
    return c
  }

  // The endings counted in the shared vocabulary, for the opening line. The
  // front matter keeps its `dead_ends` key: it is a machine field a diff or a
  // tool reads, and renaming it would break them for no reader's benefit.
  const endings = endingSummary(dead.map((r) => r.node))

  const out: string[] = []
  out.push('---')
  out.push(`title: ${yaml(session.seed)}`)
  out.push('tool: Pathways')
  out.push('schema: nv-markdown-3')
  out.push(`session: ${yaml(session.id)}`)
  out.push(`created: ${new Date(session.createdAt).toISOString()}`)
  out.push(`exported: ${new Date(now).toISOString()}`)
  // An empty ledger is the honest test for "nobody paid for this": a demo has no
  // rows, and a graph that ran one pass has one whatever it produced.
  out.push(`source: ${session.spend.length === 0 ? 'demo' : 'model'}`)
  out.push(`nodes: ${body.length}`)
  out.push(`dead_ends: ${dead.length}`)
  out.push(`open_gaps: ${gaps.length}`)
  // `insights` keeps its key for the same reason as `dead_ends`, though the kind
  // it counts now reads Takeaway beside synthesis, and Insight names a conjecture
  // (D333): a tool parsing the front matter should not break over a label.
  out.push(`insights: ${insights.length}`)
  out.push(`revised_by_user: ${edited.length}`)
  out.push(`folded_branches: ${collapsed.length}`)
  if (opts?.lens) out.push(`lens_at_export: ${opts.lens}`)
  if (opts?.temper) out.push(`temper_at_export: ${opts.temper}`)
  out.push(`checked_against_sources: ${checked.length}`)
  out.push(`disputed_by_a_source: ${disputed.length}`)
  out.push(`recorded_results: ${measured.length}`)
  out.push(`panels_that_disagreed: ${split.length}`)
  if (session.runs.length) out.push(`autonomous_runs: ${session.runs.length}`)
  const spent = totalSpend(session)
  const models = modelsUsed(session)
  if (models.length) {
    out.push(`models: ${models.map((m) => `${m.name} (${m.id})`).join(', ')}`)
  }
  out.push(`tokens_in: ${spent.inputTokens}`)
  out.push(`tokens_out: ${spent.outputTokens}`)
  out.push(`searches: ${spent.searches}`)
  out.push('scores: self-rated by the model; a sort order, not a probability')
  out.push('---')
  out.push('')
  out.push(`# ${head(session.seed)}`)
  out.push('')
  if (mathematicalProgressText(session).length)
    out.push(
      '## Mathematical obligations',
      '',
      ...mathematicalProgressText(session).map((s) => `- ${esc(s)}`),
      '',
    )
  for (const line of briefLines(session)) out.push(line)
  if (briefLines(session).length) out.push('')
  const mixed = models.length > 1
  out.push(
    mixed
      ? `*A reasoning graph explored in Pathways — ${body.length} nodes${endings ? `; ${endings}` : ''}; ${gaps.length} open gap${gaps.length === 1 ? '' : 's'}. Reasoned with ${modelNames(models)}; each node says which. Confidence, novelty and tension are each model's own self-rating, so they are not comparable between nodes produced by different models — a rough sort order within one model, and not a probability.*`
      : `*A reasoning graph explored in Pathways — ${body.length} nodes${endings ? `; ${endings}` : ''}; ${gaps.length} open gap${gaps.length === 1 ? '' : 's'}. Confidence, novelty and tension are the model's own self-ratings, calibrated by instruction rather than measurement: treat them as a sort order, not a probability.*`,
  )
  if (edited.length) {
    out.push('')
    out.push(
      `*${edited.length} node${edited.length === 1 ? '' : 's'} carr${edited.length === 1 ? 'ies' : 'y'} your own wording rather than the model's; each is marked where it appears.*`,
    )
  }
  if (collapsed.length) {
    out.push('')
    out.push(
      `*${collapsed.length} branch${collapsed.length === 1 ? '' : 'es'} ${collapsed.length === 1 ? 'is' : 'are'} folded away in the app. They are included here in full.*`,
    )
  }

  // The question's own survey, under the question it searched: the one record in
  // the graph about the seed, which the tree below never prints as a section. In
  // the report's blocks, because two documents for people wording one record two
  // ways is how they come to disagree about it. Then the surveys this record does
  // not print, dated one line each — the way a check's earlier records are dated
  // under it here, and never with their rows. A graph with no survey gets
  // neither, and is byte for byte the record it was before surveys existed.
  const seedNode = rows.find((r) => r.node.parentId === null)?.node
  const survey = seedNode && surveyLive(seedNode) ? seedNode.survey : undefined
  if (survey) {
    out.push('')
    out.push(`## ${SURVEY_HEADING}`)
    out.push('')
    out.push(blocksToMarkdown(surveyBlocks(survey)))
  }
  const earlierSurveys = seedNode ? earlierSurveyBlocks(seedNode, survey, 'this record') : []
  if (earlierSurveys.length) {
    out.push('')
    out.push(`## ${EARLIER_SURVEYS_TITLE}`)
    out.push('')
    out.push(blocksToMarkdown(earlierSurveys))
  }

  if (body.length > 12) {
    out.push('')
    out.push('## Contents')
    out.push('')
    for (const r of body) {
      const indent = '  '.repeat(Math.max(0, r.depth - 1))
      const ending = endingLabelOf(r.node)
      out.push(`${indent}- ${r.num} ${esc(r.node.label)}${ending ? ` — ${ending}` : ''}`)
    }
  }

  out.push('')
  out.push('---')

  let saidDeep = false
  for (const r of rows) {
    const n = r.node
    if (n.parentId === null) continue
    // Markdown stops at six levels. Past that the heading level *repeats* rather
    // than nesting the node as a list: a list item whose body is emitted at
    // column zero closes the list, and the next item's indentation then reads as
    // an indented code block. The section number carries the real depth.
    const level = Math.min(r.depth + 1, 6)
    out.push('')
    if (r.depth + 1 > 6 && !saidDeep) {
      saidDeep = true
      out.push(
        '*Below this point the tree is deeper than Markdown has heading levels. The headings stop nesting; the section numbers still carry the depth.*',
      )
      out.push('')
    }
    out.push(`${'#'.repeat(level)} ${r.num} ${head(n.label)}`)
    out.push('')
    out.push(metaLine(n, prod, mixed))

    if (n.bridgeFrom && n.bridgeFrom.length >= 2) {
      const otherId = n.bridgeFrom.find((b) => b !== n.parentId)
      const other = otherId ? session.nodes[otherId] : undefined
      out.push('')
      out.push(
        other
          ? `*Synthesis · fuses this branch with **${esc(other.label)}**${numOf.get(other.id) ? ` (${numOf.get(other.id)})` : ''}.*`
          : '*Synthesis · fuses with a branch that has since been pruned.*',
      )
    }
    // The other side of a synthesis names it too, so the reference reads in both
    // directions and neither section has to be read first.
    const landed = rows.find(
      (o) => o.node.bridgeFrom && o.node.bridgeFrom.includes(n.id) && o.node.parentId !== n.id,
    )
    if (landed) {
      out.push('')
      out.push(
        `*Bridged into **${esc(landed.node.label)}** (${landed.num}), under ${esc(session.nodes[landed.node.parentId ?? '']?.label ?? 'another branch')}.*`,
      )
    }
    if (n.collapsed) {
      const k = printedUnder(n.id)
      out.push('')
      out.push(
        `*Folded away in the app. The ${k} move${k === 1 ? '' : 's'} below are included here anyway.*`,
      )
    }

    out.push('')
    out.push(nodeBody(n, session).join('\n\n'))
  }

  out.push(...sweepSection(session, 'Field sweeps'))

  out.push('')
  out.push('---')

  if (measured.length || disputed.length) {
    out.push('')
    out.push('## Where this touched the world')
    out.push('')
    out.push(
      'The only statements in this document that did not come from the model. Everything else here is reasoning; these are records.',
    )
    if (measured.length) {
      out.push('')
      out.push('**Results recorded by hand.**')
      out.push('')
      out.push(
        measured
          .map(
            (r) =>
              `- **${esc(r.node.label)}** (${r.num}) — ${BEARING_LABEL[r.node.outcome!.bearing].toLowerCase()}, observed ${fmt(r.node.outcome!.at)}.`,
          )
          .join('\n'),
      )
    }
    if (disputed.length) {
      out.push('')
      out.push('**Claims sources state the opposite of.**')
      out.push('')
      out.push(
        disputed
          .map(
            (r) =>
              `- **${esc(r.node.label)}** (${r.num}) — checked ${fmt(r.node.verification!.checkedAt)}.`,
          )
          .join('\n'),
      )
    }
  }

  appendRemovedVerificationSection(out, session)

  if (dead.length) {
    out.push('')
    out.push('## Where the reasoning stopped')
    out.push('')
    out.push(
      `${dead.length} path${dead.length === 1 ? ' was' : 's were'} closed in reasoning — ${endings}. Each carries the ending it reached and the reason the model gave; only a recorded result answers one.`,
    )
    out.push('')
    out.push(
      dead
        .map(
          (r) =>
            `- **${esc(r.node.label)}** (${r.num}) — ${ENDING_LABEL[reasonedEndingOf(r.node)!]}: ${escBlock(r.node.terminalReason || 'Reasoned out.')}`,
        )
        .join('\n'),
    )
  }

  const openLeaves = body.filter(
    (r) => !r.node.terminal && (kids.get(r.node.id) ?? []).length === 0,
  )
  const steps: string[] = []
  const seenStep = new Set<string>()
  for (const r of body) {
    for (const s of nextStepsOf(r.node)) {
      if (seenStep.has(s)) continue
      seenStep.add(s)
      steps.push(`- ${escBlock(s)} — ${esc(r.node.label)} (${r.num})`)
    }
  }
  if (openLeaves.length || steps.length) {
    out.push('')
    out.push('## What is still open')
    if (openLeaves.length) {
      out.push('')
      out.push('**Ends nobody has taken further.**')
      out.push('')
      out.push(openLeaves.map((r) => `- **${esc(r.node.label)}** (${r.num})`).join('\n'))
    }
    if (steps.length) {
      out.push('')
      out.push('**What would move it.**')
      out.push('')
      out.push(steps.join('\n'))
    }
  }

  if (session.bridges.length) {
    out.push('')
    out.push('## Candidate syntheses')
    out.push('')
    out.push('Pairs the scan proposed and you have not realised. Ranked by its own score.')
    out.push('')
    // End-aware, and it prints a pruned end rather than skipping the row: a
    // candidate the scan paid for is part of the record even when one side of it
    // is gone, and a silently shorter list is a dishonest one.
    const endName = (e: BridgeEnd): string => {
      if (e.where === 'foreign') {
        return `${esc(e.ref.label)} (from your exploration of “${esc(e.ref.seed)}”)`
      }
      const n = session.nodes[e.nodeId]
      return n ? `${esc(n.label)} (${numOf.get(n.id) ?? '—'})` : '_a branch since pruned_'
    }
    for (const b of sortBridges(session.bridges as Bridge[], session.nodes)) {
      out.push(
        `- **${escBlock(b.title)}** — ${endName(b.a)} × ${endName(b.b)} · ${standingLine(b, session.nodes)}`,
      )
      out.push(`  ${escBlock(b.why)}`)
    }
  }

  if (detached.length) {
    out.push('')
    out.push('## Detached')
    out.push('')
    out.push('Nodes the graph holds but the tree cannot reach from the seed.')
    out.push('')
    out.push(detached.map((n) => `- **${esc(n.label)}** — ${escBlock(n.claim)}`).join('\n'))
  }

  if (session.ops.length) {
    out.push('')
    out.push('## How this was made')
    out.push('')
    // The Model column is printed always, even on a single-model graph: a table
    // whose columns change shape between two documents is harder to read than one
    // extra column, and a demo op's em dash is itself the information.
    out.push('| # | Move | On | Model | Produced | Finished |')
    out.push('| --- | --- | --- | --- | --- | --- |')
    session.ops.forEach((op, i) => {
      const target = op.targetIds[0] ? session.nodes[op.targetIds[0]] : undefined
      // The root is deliberately absent from `numOf`, and the demo's opening op
      // lists it in `produced` — counting it would make every demo export claim
      // a node had been removed.
      const claimed = op.produced.filter(
        (id) => session.nodes[id]?.parentId !== null || !session.nodes[id],
      )
      const nums = claimed.map((id) => numOf.get(id)).filter(Boolean) as string[]
      const gone = claimed.length - nums.length
      // Ops are carried forward across an undo and are never touched by a
      // prune, so ids that no longer resolve are normal rather than a bug.
      const cell = op.error
        ? `nothing — ${esc(op.error)}`
        : claimed.length === 0
          ? 'nothing'
          : nums.length === 0
            ? `${gone} since removed`
            : `${nums.length} node${nums.length === 1 ? '' : 's'} — ${nums.join(', ')}` +
              (gone ? ` (+${gone} since removed)` : '')
      const moveName = op.portfolio
        ? `Portfolio — ${op.portfolio.parts.map((part) => OP_LABEL[part.kind]).join(' + ')}`
        : opLabel(op, session)
      out.push(
        `| ${i + 1} | ${moveName} | ${target ? esc(target.label) : '—'} | ${op.model ? esc(op.model.name) : '—'} | ${cell}${op.portfolio?.droppedForSize ? ` (${op.portfolio.droppedForSize} dropped at the run's node allowance)` : ''} | ${op.finishedAt ? fmt(op.finishedAt) : 'in progress'} |`,
      )
    })
  }

  // Between the ledger and the traces, so it reads as provenance rather than as
  // findings: what ran, and by what rule — not what came out.
  if (session.runs.length) {
    out.push('')
    out.push('## What ran on its own')
    out.push('')
    out.push(
      'A run spends a budget on moves. It does not decide when the graph has found something, and nothing in this section is a finding — the rows below are the moves it made and the rule that chose each one.',
    )
    session.runs.forEach((r, i) => {
      const ran = r.finishedAt
        ? Math.max(1, Math.round((r.finishedAt - r.startedAt) / 60000))
        : null
      const openTotal = r.open.reachable + r.open.pastDepth + r.open.folded
      const nothing = r.moves.filter((m) => m.outcome === 'nothing').length
      const refusedAngles = r.moves.filter((m) => m.outcome === 'refused').length
      const refused = new Set(r.moves.filter((m) => m.outcome === 'refused').map((m) => m.opId))
        .size
      const failedAngles = r.moves.filter((m) => m.outcome === 'error').length
      const failed = new Set(r.moves.filter((m) => m.outcome === 'error').map((m) => m.opId)).size
      const incomplete = r.moves.filter((m) => m.outcome === 'incomplete').length
      const passes = modelPassesFor(r.moves)
      out.push('')
      // The minutes clause prints only for a run that HAD a clock: the whole-run
      // clock was removed in favour of a per-pass deadline, so a stored budget may
      // or may not carry one, and `NaN minutes` is not a ceiling. Panels and
      // cross-examinations are ceilings the run was given like any other, so a
      // budget that carries them names them here.
      out.push(
        `**Run ${i + 1}** — ${r.moves.length} of ${r.budget.moves} logical moves across ${passes} model pass${passes === 1 ? '' : 'es'}, started ${fmt(r.startedAt)}${ran ? `, ran ${ran} min` : ''}, ${esc(r.model.name)}. Ceilings it was given: ${r.budget.moves} moves, ${r.budget.outputTokens.toLocaleString()} output tokens, ${r.budget.ms !== undefined ? `${Math.round(r.budget.ms / 60000)} minutes, ` : ''}${r.budget.nodes} nodes, ${r.budget.checks} source checks${r.budget.panels ? `, ${r.budget.panels} panel${r.budget.panels === 1 ? '' : 's'}` : ''}${r.budget.audits ? `, ${r.budget.audits} cross-examination${r.budget.audits === 1 ? '' : 's'}` : ''}${r.budget.conjectures ? `, ${r.budget.conjectures} insight pass${r.budget.conjectures === 1 ? '' : 'es'}` : ''}${r.budget.usd !== undefined ? `, ${fmtUsd(r.budget.usd)} of estimated spend` : ''}. ${r.stop ? `Stopped because: ${RUN_STOP_HEAD[r.stop].toLowerCase()}` : 'No ending was recorded — the page closed while it was going.'}`,
      )
      // The spend line stands alone rather than joining the counts below, because
      // it is the one figure in this block that is an estimate and not a count —
      // and inside a `·`-joined list of exact numbers it would read as one of them.
      if (r.estimatedUsd !== undefined) {
        out.push('')
        out.push(
          `It drew about ${fmtUsd(r.estimatedUsd)}${r.budget.usd !== undefined ? ` of its ${fmtUsd(r.budget.usd)} ceiling` : ''} — estimated at list prices read on ${PRICE_READ_ON}, each token at the rate its kind bills at and anything a receipt did not break down at the full rate, with no discount or credit counted, so the real charge was no higher and may have been lower. Not a bill.`,
        )
      }
      // Beside the spend line, because it is the ceiling's doing: the Run tab and
      // the closing toast print the same sentence.
      if (r.checksSkipped) {
        out.push('')
        out.push(checksSkippedLine(r.checksSkipped))
      }
      if (r.checksUnread) {
        out.push('')
        out.push(checksUnreadLine(r.checksUnread))
      }
      out.push('')
      const bits = [
        `${r.produced} node${r.produced === 1 ? '' : 's'}`,
        runEndingsLine(r),
        nothing ? `${nothing} move${nothing === 1 ? '' : 's'} returned nothing new` : null,
        refused
          ? `${refusedAngles} logical angle${refusedAngles === 1 ? '' : 's'} declined across ${refused} model pass${refused === 1 ? '' : 'es'}`
          : null,
        failed
          ? `${failedAngles} logical angle${failedAngles === 1 ? '' : 's'} failed across ${failed} model pass${failed === 1 ? '' : 'es'}`
          : null,
        incomplete
          ? `${incomplete} portfolio angle${incomplete === 1 ? '' : 's'} incomplete`
          : null,
        r.checkReadings.length
          ? `${r.checkReadings.length} source check${r.checkReadings.length === 1 ? '' : 's'}`
          : null,
        r.scanned !== undefined
          ? `${r.scanned} candidate pair${r.scanned === 1 ? '' : 's'} left unrealised`
          : null,
        r.declined
          ? `${r.declined} branch${r.declined === 1 ? '' : 'es'} you closed while it ran`
          : null,
      ].filter(Boolean)
      out.push(bits.join(' · '))
      out.push('')
      // Whatever the count. A run whose whole output rests on the model is exactly
      // the run that must say so, and a row omitted at zero says nothing.
      const read = runReadings(r, session)
      out.push(
        read.length
          ? `${read.length} claim${read.length === 1 ? '' : 's'} went to a source check — ${read.map((x) => READING_LABEL[x].toLowerCase()).join(', ')}. Every other node it added rests on the model alone.`
          : 'No claim this run added was put in front of a source. All of it rests on the model alone.',
      )
      out.push('')
      out.push(
        `${openTotal} open end${openTotal === 1 ? '' : 's'} remained: ${r.open.reachable} this policy could still have taken, ${r.open.pastDepth} past the depth a run works to, ${r.open.folded} inside folded branches.`,
      )
      if (r.moves.length) {
        out.push('')
        out.push('| # | Move | On | Chosen because | Result |')
        out.push('| --- | --- | --- | --- | --- |')
        for (const m of r.moves) {
          // The shared wording, plus this surface's own note. An exported document is
          // read away from the app, so a declined or failed move carries the reason
          // inline where the account can afford to put it on a second line.
          const result =
            m.outcome === 'refused'
              ? `the model declined${m.note ? ` — “${esc(m.note)}”` : ''}`
              : m.outcome === 'error'
                ? `${STATUS_WORD.didNotRun.toLowerCase()}${m.note ? ` — ${esc(m.note)}` : ''}`
                : moveResult(m)
          out.push(
            `| ${m.at} | ${OP_LABEL[m.kind]} | ${m.label ? esc(m.label) : '—'} | ${MOVE_REASON[m.why]} | ${result} |`,
          )
        }
      }
    })
  }

  if (opts?.thinking !== false) {
    const traced = session.ops.filter((o) => !!o.thinking)
    if (traced.length) {
      out.push('')
      out.push('## The model’s thinking')
      out.push('')
      out.push(
        'One trace per pass. A pass deliberates once and can emit several moves, so a trace belongs to all of them.',
      )
      for (const op of traced) {
        const nums = op.produced.map((id) => numOf.get(id)).filter(Boolean) as string[]
        out.push('')
        out.push('<details>')
        out.push(
          `<summary>${opLabel(op, session)}${nums.length ? ` → ${nums.join(', ')}` : ''}</summary>`,
        )
        // The blank lines are required, or the block below is treated as raw
        // HTML and never rendered.
        out.push('')
        out.push(
          op
            .thinking!.split('\n\n')
            .map((para) => `> ${escBlock(para).replace(/\n/g, '\n> ')}`)
            .join('\n>\n'),
        )
        out.push('')
        out.push('</details>')
      }
    }
  }

  out.push('')
  out.push('---')
  out.push('')
  out.push(
    `*Exported from Pathways on ${fmt(now)}. Every branch is included, whatever the display filters, the timeline or the folds were set to.*`,
  )
  return `${out.join('\n')}\n`
}

/* ── document (c): the research report ─────────────────────────────────────
   The whole-graph export above is a TREE: it walks the argument in the order it
   arrived and prints everything. That is the right shape for a record and the
   wrong one for reading, because tree position has nothing to do with weight.

   This one is a research brief, and its sections, their order and every sentence
   in them come from `reportDocument` in `reportSections.ts` — the same derivation
   the PDF sets in type. What lives here is only the Markdown: escapes, emphasis,
   list and table syntax. A section added there appears in both formats or in
   neither, which is the property the two used to lose one edit at a time. */

/** The Markdown emphasis each run style takes. `mono` is the PDF's amber figure,
 *  which Markdown has no form for, so it reads as body text. */
const MARK: Record<RunStyle, string> = {
  plain: '',
  bold: '**',
  italic: '*',
  quiet: '*',
  mono: '',
  link: '',
}

/**
 * Rich text as one line of Markdown.
 *
 * Neighbouring runs that take the same emphasis are merged first, so a quiet run
 * followed by a quiet run is one `*…*` rather than `*a**b*`, which renders as
 * neither. Whitespace is moved outside the markers, because `*a *` is not
 * emphasis anywhere. `keepLines` keeps a plain run's own line breaks, for a
 * summary written in paragraphs; emphasis never spans one.
 */
function inline(rich: Rich, keepLines = false): string {
  const merged: { mark: string; text: string }[] = []
  for (const run of rich) {
    const mark = MARK[run.style ?? 'plain']
    const text = run.text.replace(/\r\n/g, '\n')
    const last = merged[merged.length - 1]
    if (last && last.mark === mark) last.text += text
    else merged.push({ mark, text })
  }
  return merged
    .map(({ mark, text }) => {
      const body = esc(keepLines && !mark ? text : text.replace(/\s*\n+\s*/g, ' '))
      if (!mark) return body
      const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(body)!
      return m[2] ? `${m[1]}${mark}${m[2]}${mark}${m[3]}` : body
    })
    .join('')
}

/** A paragraph: `inline`, plus what stops any of its lines being read as block
 *  syntax — the same guard `escBlock` applies. */
function paragraph(rich: Rich): string {
  return inline(rich, true)
    .trim()
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((line) => (/^(#|-|\+|>|\d+\.)/.test(line) ? `\\${line}` : line))
    .join('\n')
}

/** A kept source. A web page is its title as a link with the host beside it —
 *  the report is pasted where nobody can hover a link to see where it goes. A
 *  passage is its document's title in bold, linked only where the record has a
 *  public address: a file on the reader's machine has none, and inventing one
 *  would be the first dishonest link in the document. */
function sourceMarkdown(x: SourceLine): string {
  if (x.passage) {
    return `- ${x.url ? `[**${esc(x.title)}**](${x.url})` : `**${esc(x.title)}**`}, ${esc(x.note)}`
  }
  return `- [${esc(x.title)}](${x.url}) — ${esc(x.note)}`
}

/** A ledger's title is set in capitals on the page; as a heading it is a sentence. */
const sentenceCase = (t: string): string => t.charAt(0) + t.slice(1).toLowerCase()

/** The shared blocks as Markdown. The eyebrows are the PDF's alone. */
function blocksToMarkdown(blocks: Block[]): string {
  const chunks: { text: string; item: boolean }[] = []
  for (const b of blocks) {
    let text: string | null = null
    switch (b.b) {
      case 'eyebrow':
        break
      case 'title':
        text = `# ${head(b.text)}`
        break
      case 'counts':
        text = b.items.map((i) => esc(i.text)).join(' · ')
        break
      case 'lede':
      case 'para':
        text = paragraph(b.rich)
        break
      case 'h2':
        text = `${b.part ? '#' : '##'} ${head(b.text)}`
        break
      case 'h3':
        text = `### ${head(b.text)}`
        break
      case 'bullet':
        text = `- ${inline(b.rich)}${(b.sub ?? []).map((x) => `\n  - ${inline(x)}`).join('')}`
        break
      case 'quotePanel':
        text = b.paras.map((x) => `- ${inline(x)}`).join('\n')
        break
      case 'card': {
        const top = [`**${esc(b.title)}**`, ...b.chips.map((c) => esc(c.text))]
        if (b.score) top.push(`${(b.scoreCaption ?? 'score').toLowerCase()} ${b.score}`)
        const parts = [top.join(' · '), ...b.paras.map(paragraph)]
        if (b.sources?.length) parts.push(b.sources.map(sourceMarkdown).join('\n'))
        text = parts.join('\n\n')
        break
      }
      case 'table':
        text = [
          `| ${b.head.map(esc).join(' | ')} |`,
          `| ${b.head.map(() => '---').join(' | ')} |`,
          ...b.rows.map((r) => `| ${r.map(esc).join(' | ')} |`),
        ].join('\n')
        break
      case 'ledger':
        text = `## ${head(sentenceCase(b.title))}\n\n${b.bullets.map((x) => `- ${inline(x)}`).join('\n')}`
        break
      case 'rule':
        text = '---'
        break
      case 'footer':
        text = `*${esc(b.text)}*`
        break
    }
    if (text !== null) chunks.push({ text, item: b.b === 'bullet' })
  }
  // Consecutive bullets are one list; everything else is its own paragraph.
  let out = ''
  chunks.forEach((c, i) => {
    if (i > 0) out += c.item && chunks[i - 1]!.item ? '\n' : '\n\n'
    out += c.text
  })
  return out
}

export function reportMarkdown(session: Session, opts?: MarkdownOptions): string {
  const now = opts?.now ?? Date.now()
  const doc = reportDocument(session, opts)
  const text = blocksToMarkdown([
    { b: 'title', text: session.seed },
    { b: 'counts', items: doc.header },
    { b: 'lede', rich: [{ text: doc.lede, style: 'italic' }] },
    ...doc.preamble,
    ...reportBody(doc),
    { b: 'rule' },
    { b: 'footer', text: `Exported from Pathways on ${fmt(now)}.` },
  ])
  return `${text}\n`
}

/**
 * The frame and standing brief, as export lines — or none when a session has
 * neither, so every pre-existing document renders byte-identically.
 *
 * In every format that prints the seed as its heading, directly under it,
 * because both change what the whole document IS: a goal's branches are routes
 * and not truth-claims, and a document that listed conclusions without the
 * stipulations they rest on would read as having established what it assumed —
 * which is why every given is printed with the word "stipulated" attached.
 */
function briefLines(session: Session): string[] {
  const out: string[] = []
  if (session.frame === 'question' || session.frame === 'goal') {
    out.push(`*${FRAME_NOTE[session.frame]}*`)
  }
  if (session.brief?.length) {
    if (out.length) out.push('')
    // "For every pass" only while it is true: a directive typed or answered on a
    // later day reached none of the passes before it (`briefLateNote`).
    out.push(`**The standing brief** — ${briefLeadClause(session)}`)
    out.push('')
    for (const d of session.brief) {
      const tag =
        d.kind === 'given'
          ? 'Given (STIPULATED by the user, not established — findings resting on it evaporate if it does)'
          : DIRECTIVE_LABEL[d.kind]
      const late = briefLateNote(session, d, fmt)
      out.push(`- ${tag}: ${esc(d.text)}${late ? ` *(${late})*` : ''}`)
    }
  }
  return out
}

/* ── document (d): a briefing for another model ─────────────────────────────
   Meant to be pasted into a chat with a different model and continued.

   Two things make this a different document rather than a smaller one:

   It OPENS with instructions to the reader, because the reader is a model and
   will otherwise do the two things this tool spends its whole design preventing —
   treat self-rated confidence as probability, and treat unchecked model output as
   established. A human reader arrives at the caveats at the end and is fine; a
   model needs them before the content or it has already formed a view.

   And it separates what was CHECKED from what was ASSERTED explicitly, as two
   lists, rather than leaving the distinction implicit in per-node annotations. A
   receiving model given one undifferentiated tree will flatten it into "things
   that are true about this topic" — which is exactly the artefact the graph
   exists to avoid becoming. */

/** The survey's heading in the briefing: what it is, before a single row. */
export const SURVEY_HANDOFF_HEADING = 'Survey of the field — search results, not established fact'

/** And what that means for the reader, in terms of the instructions above it. */
export const SURVEY_HANDOFF_NOTE =
  'A model searched the question itself once and wrote down what came back: a record of what one search returned about the question, not an answer to it and not a check of any claim below, so none of it counts as “Checked against sources” under instruction 1. Each sentence is the surveying model’s account of the sources listed after it, and an absence here is an absence in these results, not in the literature.'

/**
 * A digest record — plain lines, where a row's own `- ` is the only syntax — as
 * Markdown: every line escaped as the rest of the briefing is, a part's rows
 * kept as one list, and a blank line everywhere else, so the next part's heading
 * is not read as the last row's continuation. A row's text is guarded as a block
 * too: `- # …` is a heading inside a list item.
 */
function recordMarkdown(text: string): string {
  const lines = text.split('\n')
  return lines
    .map((line, i) => {
      const item = line.startsWith('- ')
      const md = item ? `- ${escBlock(line.slice(2))}` : escBlock(line)
      if (i === 0) return md
      return `${item && lines[i - 1]!.startsWith('- ') ? '\n' : '\n\n'}${md}`
    })
    .join('')
}

/**
 * The proposition a recorded result was recorded ABOUT, and what the node says
 * now where the two differ.
 *
 * Printing only the recorded wording leaves a reader comparing it against the
 * node's own claim elsewhere in the same document with nothing to explain the
 * difference; printing only the current claim is the defect, because the
 * document's own copy says these statements "outrank everything else". So: the
 * measured proposition is the recorded one, and `supersededBy` carries what the
 * node says now, for the sentence that says so.
 *
 * A legacy record with no snapshot has nothing to offer but the current claim,
 * and `outcomeLive` already answers `true` for it.
 */
export function measuredClaim(n: ReasonNode): { claim: string; supersededBy: string | null } {
  const recorded = n.outcome?.claimAtRecord
  if (recorded === undefined || outcomeLive(n)) return { claim: n.claim, supersededBy: null }
  return { claim: recorded, supersededBy: n.claim }
}

export function briefingMarkdown(session: Session, opts?: MarkdownOptions): string {
  const now = opts?.now ?? Date.now()
  const { rows } = walk(session)
  const body = rows.filter((r) => r.node.parentId !== null)
  const models = modelsUsed(session)
  const measured = body.filter((r) => !!r.node.outcome)
  const checked = body.filter((r) => checkedAgainstSources(r.node) && verifiedLive(r.node))
  const openEnds = body.filter(
    (r) => !r.node.terminal && !childrenIndex(session).get(r.node.id)?.length,
  )

  const out: string[] = []

  out.push('# Reasoning graph handoff')
  out.push('')
  out.push('## Read this before the content')
  out.push('')
  out.push(
    'You are being handed a reasoning graph built in Pathways: one question, taken apart into individual claims, each of which is meant to be arguable rather than true. It was produced by a model, mostly unverified, and it is a MAP OF WHERE TO LOOK rather than a set of findings. Five instructions, and they matter more than anything below them:',
  )
  out.push('')
  out.push(
    [
      '1. **Do not treat any claim here as established** unless it appears under “Checked against sources” or “Measured” below. Everything else is one model’s reasoning, written to be attackable. Repeating it back as fact is the single most likely way to be wrong with this document.',
      '2. **The numbers are not probabilities.** Where a claim shows `conf`, `nov` or `tens`, those are the producing model’s own self-ratings on its own instructed scale. They sort claims within this graph and mean nothing outside it. Never convert one into a percentage, a likelihood, or a confidence interval.',
      '3. **A recorded result outranks every claim here, including its own parent.** Where you see `MEASURED`, a person entered an observation from the world. Do not argue it away and do not average it against the model’s confidence.',
      '4. **Citations in this document were gated; yours are not.** Every source named here was returned by a search API and checked against that set, and a quoted sentence marked `quote-checked` was matched against the page’s own text. If you add sources of your own, say plainly that they are recollections unless you actually retrieved them.',
      // The markers are quoted from the table the `CLOSED:` markers below are
      // printed from, so this instruction cannot name an ending the tree no
      // longer prints.
      `5. **Endings are findings, and they are not all the same.** \`CLOSED:${ENDING_LABEL['objection-stood']}\` means an objection the model raised held against its parent — in reasoning, not by any source. \`CLOSED:${ENDING_LABEL['path-failed']}\` is a branch argued to exhaustion. \`CLOSED:${ENDING_LABEL.open}\` is a question that needs a person or the world, not more argument. \`ANSWERED\` means a recorded result settled it. Do not revive a closed path to be helpful; if you think the reasoning behind it is wrong, say which step and why.`,
    ].join('\n'),
  )
  out.push('')
  out.push(
    'The person handing you this wants to keep working on it. They will tell you what they want; do not assume it is a summary.',
  )

  out.push('')
  out.push('## The question')
  out.push('')
  out.push(`> ${escBlock(session.seed).replace(/\n/g, '\n> ')}`)
  out.push('')
  for (const line of briefLines(session)) out.push(line)
  if (briefLines(session).length) out.push('')
  out.push(
    `${body.length} claim${body.length === 1 ? '' : 's'} derived from it${models.length ? `, by ${modelNames(models)}` : ''}. ${measured.length} carr${measured.length === 1 ? 'ies' : 'y'} a recorded observation; ${checked.length} ha${checked.length === 1 ? 's' : 've'} been checked against sources; the remaining ${Math.max(0, body.length - measured.length - checked.length)} rest on the model alone.`,
  )

  /* The question's own survey, beside the question it searched: live only, and
     in the digest's words for a survey pass (`surveyRecord`), which were written
     for a model reader. Said to be search results in its heading, where the
     reader meets it first — a survey's landmark results, unlabelled, read as the
     question answered, and a receiving model that took them so would have done
     what instruction 1 exists to stop. Its kept sources follow in the form a
     check's take below. With no live survey there is nothing here at all. */
  const seed = rows.find((r) => r.node.parentId === null)?.node
  if (seed && surveyLive(seed)) {
    const sv = seed.survey!
    out.push('')
    out.push(`## ${SURVEY_HANDOFF_HEADING}`)
    out.push('')
    out.push(SURVEY_HANDOFF_NOTE)
    out.push('')
    out.push(recordMarkdown(surveyRecord(sv)))
    out.push('')
    out.push(
      `Sources: ${
        sv.sources.length
          ? sv.sources.map((x) => `${esc(surveySourceTitle(x))} <${x.url}>`).join('; ')
          : 'none survived the citation gate'
      }.`,
    )
  }

  /* The two explicit lists, before the tree. A receiving model that reads these
     first cannot flatten the tree into undifferentiated fact afterwards. */
  out.push('')
  out.push('## Measured — observations a person recorded')
  out.push('')
  if (!measured.length) {
    out.push('None. Nothing in this graph has been tested against the world.')
  } else {
    for (const r of measured) {
      const o = r.node.outcome!
      const measuredAs = measuredClaim(r.node)
      out.push(
        `- **[${r.node.id}] ${esc(r.node.label)}** — ${BEARING_LABEL[o.bearing].toLowerCase()}, observed ${fmt(o.at)}. Claim as measured: ${escBlock(measuredAs.claim)}${measuredAs.supersededBy !== null ? ` Since rewritten, now unmeasured: ${escBlock(measuredAs.supersededBy)}` : ''} Observation: ${escBlock(o.observed)}`,
      )
    }
  }

  out.push('')
  out.push('## Checked against sources')
  out.push('')
  if (!checked.length) {
    out.push(
      'None. No claim in this graph has been put in front of a source, so treat the whole of it as one model’s reasoning.',
    )
  } else {
    for (const r of checked) {
      const v = r.node.verification!
      const q = v.sources.filter((x) => x.quoteFound === true).length
      const ledger = evidenceLedgerNotes(r.node)
      out.push(
        `- **[${r.node.id}] ${esc(r.node.label)}** — reading: ${READING_LABEL[v.reading].toLowerCase()}. ${v.summary ? escBlock(v.summary) : ''} Sources: ${
          v.sources.length
            ? v.sources
                .map(
                  (x) =>
                    `${esc(x.title)} <${x.url}> (${stanceLabel(x)}${x.design ? `, ${DESIGN_WORD[x.design][0]}` : ''})`,
                )
                .join('; ')
            : 'none survived the citation gate'
        }.${q ? ` ${q} quote-checked against the page.` : ''}${retractedLine(r.node)}${v.scope === 'corpus' ? ' Read only the user’s own documents — absence in them is not absence in the literature.' : ''}${ledger.length ? ` ${ledger.join(' ')}` : ''}`,
      )
    }
  }

  appendRemovedVerificationSection(out, session)

  /* The graph itself, as an outline. Reasoning is included: it is the substance,
     and a briefing that dropped it would hand over a table of contents. Traces
     are not — they are the deliberation, not the argument. */
  out.push('')
  out.push('## The graph')
  out.push('')
  out.push(
    `Indented by depth. Each line is \`[id] label — claim\`, then the reasoning, then compact markers. Markers: \`KIND\` is the move type; \`CLOSED:x\` is how the model closed a path (see instruction 5); \`ANSWERED\` is a recorded result that settled it; \`CHECKED:x\` is a source reading; \`MEASURED\` is a recorded observation; ${
      // Keyed only where a claim carries it: a legend line about refutations in a
      // graph with none would put the word in front of a reader with nothing to
      // attach it to.
      rows.some((r) => taintMap(session).has(r.node.id))
        ? `\`${STANDS_ON_MARK}:id\` means it was reasoned from claim [id] before a source check or a recorded result refuted that claim, and has not been re-derived since — do not build on it as though that premise stood; `
        : ''
    }\`SPLIT\` means three separate passes disagreed about it; \`YOURS\` means the human rewrote the claim themselves and it is authoritative.`,
  )
  out.push('')
  for (const r of rows) {
    const n = r.node
    if (n.parentId === null) continue
    const pad = '  '.repeat(Math.max(0, r.depth - 1))
    // Absent on a claim the reader rewrote: its numbers rated words that are gone.
    const rated = selfRatingsOf(n)
    const standsOn = refutedUnder(session, n.id)
    const marks = [
      n.kind.toUpperCase(),
      reasonedEndingOf(n) ? `CLOSED:${ENDING_LABEL[reasonedEndingOf(n)!]}` : null,
      endingOf(n) === 'answered' ? 'ANSWERED' : null,
      // The same liveness rule `CHECKED` gets on the next line, and the one the
      // Measured section above already prints under. This document tells the
      // reading model, in its own instructions, that a `MEASURED` claim
      // "outranks every claim here" and must not be argued away — so stamping
      // one on a claim somebody has rewritten since the observation hands that
      // authority to a proposition nobody tested, and does it after the section
      // above has just said the claim is unmeasured.
      n.outcome && outcomeLive(n) ? 'MEASURED' : null,
      n.verification && verifiedLive(n) ? `CHECKED:${n.verification.reading}` : null,
      standsOn ? `${STANDS_ON_MARK}:${standsOn.source.id}` : null,
      panelSplit(n.panel) ? 'SPLIT' : null,
      n.editedAt ? 'YOURS' : null,
      rated
        ? `conf ${fmtRating(rated.confidence)} nov ${fmtRating(rated.novelty)} tens ${fmtRating(rated.tension)}`
        : null,
      mixedModel(session) && n.model
        ? n.editedAt
          ? `first proposed by ${n.model.name}`
          : n.model.name
        : null,
    ]
      .filter(Boolean)
      .join(' · ')
    out.push(`${pad}- **[${n.id}] ${esc(n.label)}** — ${escBlock(n.claim)}`)
    const reasoning = reasoningOf(n)
    out.push(...nodeTextRetentionNotes(n).map((note) => `${pad}  ${escBlock(note)}`))
    if (reasoning) out.push(`${pad}  ${escBlock(reasoning).replace(/\n/g, ' ')}`)
    if (n.compact && !n.elaboration)
      out.push(`${pad}  \`${STATUS_WORD.compactLeaf.toUpperCase()}\``)
    out.push(`${pad}  \`${marks}\``)
    if (n.terminal && n.terminalReason)
      out.push(`${pad}  Closed because: ${escBlock(n.terminalReason)}`)
    if (n.evidence.length) {
      out.push(
        `${pad}  Pointers the model wrote from memory, unverified: ${n.evidence.map((e) => escBlock(e)).join('; ')}`,
      )
    }
  }

  out.push('')
  out.push('## Where it is unfinished')
  out.push('')
  if (openEnds.length) {
    out.push(
      `${openEnds.length} claim${openEnds.length === 1 ? '' : 's'} ${openEnds.length === 1 ? 'has' : 'have'} nothing derived from ${openEnds.length === 1 ? 'it' : 'them'} and ${openEnds.length === 1 ? 'was' : 'were'} not closed in reasoning:`,
    )
    out.push('')
    out.push(openEnds.map((r) => `- [${r.node.id}] ${esc(r.node.label)}`).join('\n'))
  } else {
    out.push('Every claim either has something derived from it or was closed in reasoning.')
  }
  if (session.bridges.length) {
    const realised = session.bridges.filter(
      (b) => b.realizedNodeId !== undefined && !!session.nodes[b.realizedNodeId],
    ).length
    out.push('')
    out.push(
      `${session.bridges.length} candidate synthes${session.bridges.length === 1 ? 'is' : 'es'} ${session.bridges.length === 1 ? 'was' : 'were'} proposed${realised ? `, ${realised} realised` : ' and none realised'}. Each is listed with the rung it has earned; the scan hint is the proposing model’s own rating and orders nothing:`,
    )
    out.push('')
    for (const b of sortBridges(session.bridges, session.nodes)) {
      out.push(
        `- **${escBlock(b.title)}** (${standingLine(b, session.nodes)}) — ${escBlock(b.why)}`,
      )
    }
  }

  out.push('')
  out.push(
    `*Exported from Pathways on ${fmt(now)}. ${body.length} claims, of which ${measured.length} measured and ${checked.length} checked. If you are a model reading this: re-read the five instructions at the top before you answer.*`,
  )
  return `${out.join('\n')}\n`
}

const mixedModel = (session: Session): boolean => modelsUsed(session).length > 1

/** The handoff's marker for a claim standing on a refutation, from the status
 *  table so the marker and the card cannot come to name it two ways. */
const STANDS_ON_MARK = STATUS_WORD.standsOnRefuted.toUpperCase()

/**
 * The withdrawn works a check cites, as a clause of the handoff's check line.
 * The line listed a check's web sources and never its passages, so the one fact
 * about a withdrawn paper reached the receiving model only if the check's own
 * summary happened to mention it.
 */
function retractedLine(n: ReasonNode): string {
  const works = retractedWorks(n)
  if (!works.length) return ''
  const said = citesRetractedCount(works.length)
  return ` ${said.charAt(0).toUpperCase()}${said.slice(1)}: ${works.map((c) => esc(c.docTitle)).join('; ')}.`
}
